import React from 'react';
import {
    School as LearnIcon,
    Groups as MentorIcon,
    Work as WorkIcon,
    Code as CodeIcon,
    DesignServices as DesignIcon,
    PhoneAndroid as MobileIcon,
    Storage as CloudIcon,
    Terminal as TerminalIcon,
    TrendingUp as GrowthIcon,
    Schedule as ScheduleIcon,
    LocationOn as LocationIcon,
    EmojiEvents as AchievementIcon
} from '@mui/icons-material';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';
import PublicIcon from '@mui/icons-material/Public';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import {
    Box,
    Button,
    Container,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    Stack,
    Typography,
    useTheme,
    alpha,
    Paper,
    Divider,
    Avatar,
    Chip
} from '@mui/material';
import { Tag } from 'antd';
import { useNavigate } from 'react-router-dom';

const InternshipPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const programs = [
        {
            icon: (
                <ProductionQuantityLimitsIcon fontSize="large" />
            ),
            title: "Digital Marketing",
            description: "Gain hands-on experience with real-world digital campaigns, SEO strategies, and analytics tools.",
            color: theme.palette.warning.main
        },
        {
            icon: <CodeIcon fontSize="large" />,
            title: "Full-Stack Development",
            description: "Build complete web applications with modern frameworks under expert guidance",
            color: theme.palette.primary.main
        },
        {
            icon: <MobileIcon fontSize="large" />,
            title: "Mobile Development",
            description: "Create iOS/Android apps with hands-on projects and code reviews",
            color: theme.palette.secondary.main
        },
        {
            icon: <DesignIcon fontSize="large" />,
            title: "UI/UX Design",
            description: "Learn design thinking and industry-standard tools from professionals",
            color: theme.palette.success.main
        },
        {
            icon: <CloudIcon fontSize="large" />,
            title: "Cloud Engineering",
            description: "Work with AWS/Azure infrastructure on real-world scenarios",
            color: theme.palette.warning.main
        },
        {
            icon: <PublicIcon fontSize="large" />,
            title: "Web Development",
            description: "Build complete applications fornt-end and backend with modern frameworks under expert guidance",
            color: theme.palette.primary.main
        },
    ];

    const internshipFeatures = [
        { label: "Duration", value: "6 Months", icon: <ScheduleIcon /> },
        { label: "Projects", value: "Live Projects", icon: <WorkIcon /> },
        { label: "Certificate", value: "Experience Certificate", icon: <AchievementIcon /> },
        { label: "Cost", value: "Free Internship", icon: <LearnIcon /> },
        { label: "Qualification", value: "Any Graduate", icon: <WorkIcon /> },
        { label: "Opportunity", value: "Job Conversion", icon: <GrowthIcon /> }
    ];

    const benefits = [
        {
            icon: <MentorIcon />,
            title: "1:1 Mentorship",
            description: "Sessions with industry experts"
        },
        {
            icon: <WorkIcon />,
            title: "Real Projects",
            description: "Contribute to actual client work"
        },
        {
            icon: <GrowthIcon />,
            title: "Career Growth",
            description: "Portfolio building & interview prep"
        },
        {
            icon: <AchievementIcon />,
            title: "Certification",
            description: "Recognized completion certificate"
        }
    ];

    const mentors = [
        {
            name: "Alex Chen",
            role: "Senior Full-Stack Developer",
            experience: "8+ years at Google & Amazon",
            avatar: "/avatars/mentor1.jpg"
        },
        {
            name: "Priya Patel",
            role: "Lead UX Designer",
            experience: "5+ years at Adobe & Figma",
            avatar: "/avatars/mentor2.jpg"
        },
        {
            name: "James Wilson",
            role: "Cloud Architect",
            experience: "Microsoft Certified Trainer",
            avatar: "/avatars/mentor3.jpg"
        }
    ];

    return (
        <Box sx={{ bgcolor: 'background.default' }}>
            {/* Hero Section */}
            <Box sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.9)} 0%, ${alpha(theme.palette.secondary.dark, 0.9)} 100%)`,
                color: 'common.white',
                py: 12,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -50,
                    left: -50,
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: alpha(theme.palette.common.white, 0.1)
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -100,
                    right: -100,
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: alpha(theme.palette.common.white, 0.1)
                }
            }}>
                <Container maxWidth="lg">
                    <Stack spacing={4} alignItems="center" textAlign="center">
                        <Chip
                        onClick={()=>console.log("")}
                            icon={<LearnIcon />}
                            label="Career Launch Program"
                            sx={{
                                bgcolor: alpha(theme.palette.common.white, 0.2),
                                color: 'common.white',
                                px: 2,
                                py: 1,
                                fontSize: '0.875rem'
                            }}
                        />

                        <Typography variant="h2" component="h1" sx={{
                            fontWeight: 800,
                            fontSize: { xs: '2.5rem', md: '3.75rem' },
                            lineHeight: 1.2
                        }}>
                            Learn from <Box component="span" sx={{ color: theme.palette.warning.light }}>Industry Experts</Box>
                        </Typography>

                        <Typography variant="h5" sx={{
                            maxWidth: 800,
                            opacity: 0.9,
                            fontWeight: 400
                        }}>
                            Our internship program pairs you with senior developers and designers for hands-on experience with real projects
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={()=>navigate('/get-in-touch')}
                                size="large"
                                endIcon={<ArrowRightIcon />}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    '&:hover': {
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                Apply Now
                            </Button>

                            <Button
                                variant="outlined"
                                color="inherit"
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderWidth: '2px',
                                    '&:hover': {
                                        borderWidth: '2px',
                                        bgcolor: alpha(theme.palette.common.white, 0.1)
                                    }
                                }}
                            >
                                Program Details
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>

            {/* Programs Section */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Stack spacing={6} alignItems="center">
                    <Typography variant="h3" component="h2" sx={{
                        fontWeight: 700,
                        position: 'relative',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: -8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '80px',
                            height: '4px',
                            bgcolor: theme.palette.primary.main,
                            borderRadius: '2px'
                        }
                    }}>
                        Our Internship Tracks
                    </Typography>

                    <Grid container spacing={4} justifyContent="center">

                        {programs.map((program, index) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                <Box
                                    sx={{
                                        height: '100%',
                                        maxWidth: 500,
                                        mx: 'auto',
                                        p: 4,
                                        borderRadius: '12px',
                                        background: alpha(program.color, 0.1),
                                        transition: 'all 0.3s ease',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: `0 10px 20px ${alpha(program.color, 0.2)}`
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            mb: 3
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                bgcolor: program.color,
                                                color: 'common.white',
                                                borderRadius: '12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {program.icon}
                                        </Box>

                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {program.title}
                                        </Typography>
                                    </Box>

                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ mb: 2, textAlign: 'center' }}
                                    >
                                        {program.description}
                                    </Typography>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Button
                                            variant="text"
                                            color="inherit"
                                            endIcon={<ArrowRightIcon />}
                                            sx={{
                                                px: 0,
                                                color: program.color,
                                                fontWeight: 600
                                            }}
                                        >
                                            Learn more
                                        </Button>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>


                </Stack>
            </Container>

            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Grid container justifyContent="center">
                    <Grid item xs={12} md={10} lg={9} justifyContent="center">
                        <Paper
                            elevation={3}
                            sx={{
                                p: { xs: 4, md: 6 },
                                borderRadius: 4,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.info.light, 0.15)}, ${alpha(
                                    theme.palette.primary.light,
                                    0.15
                                )})`,
                                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
                            }}
                        >
                            <Typography
                                variant="h4"
                                component="h3"
                                gutterBottom
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette.info.dark,
                                    textAlign: 'center',
                                    mb: 6,
                                }}
                            >
                                🚀 Internship Highlights
                            </Typography>

                            <Grid
                                container
                                spacing={4}
                                justifyContent="center"
                                alignItems="center"
                            >
                                {internshipFeatures.map((feature, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                        }}>
                                        <Paper
                                            elevation={1}
                                            sx={{
                                                p: 3,
                                                borderRadius: 3,
                                                bgcolor: 'background.default',
                                                height: '100%',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        >

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    // width: '20%',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        textAlign: 'center',
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: '50%',
                                                        bgcolor: alpha(theme.palette.info.main, 0.1),
                                                        color: theme.palette.info.main,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 24,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {feature.icon}
                                                </Box>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {feature.label}
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight={600}>
                                                        {feature.value}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                        </Paper>

                                    </Grid>
                                ))}
                            </Grid>

                            <Box sx={{ mt: 5, textAlign: 'center' }}>
                                <Tag
                                    color="processing"
                                    onClick={() => window.open('https://maps.app.goo.gl/wdJPZdjzE6bApELh8', '_blank')}
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        padding: '8px 16px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        borderRadius: 20,
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                        background: alpha(theme.palette.info.main, 0.08),
                                    }}
                                >
                                    <LocationIcon style={{ fontSize: '1.3rem' }} />
                                    <span style={{ fontWeight: 600 }}>Moodbidri</span>
                                </Tag>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            {/* Benefits Section */}
            <Box sx={{
                bgcolor: 'grey.50',
                py: 10,
                backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 100%)'
            }}>
                <Container maxWidth="lg">
                    <Stack spacing={6} alignItems="center">
                        <Typography variant="h3" component="h2" sx={{
                            fontWeight: 700,
                            textAlign: 'center'
                        }}>
                            Why Our Internship Stands Out
                        </Typography>
                        <Grid container spacing={3} justifyContent="center">
                            {benefits.map((benefit, index) => (
                                <Grid
                                    item
                                    xs={12}      
                                    sm={false}   
                                    md={3}        
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        maxWidth: 280,
                                    }}
                                >
                                    <Paper
                                        elevation={3}
                                        sx={{
                                            p: 3,
                                            width: '100%', // Takes full width of Grid item (max 280px)
                                            minHeight: 200,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textAlign: 'center',
                                            borderRadius: 3,
                                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                boxShadow: (theme) => theme.shadows[6],
                                                bgcolor: (theme) => alpha(theme.palette.primary.light, 0.05),
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                bgcolor: 'primary.main',
                                                color: 'primary.contrastText',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 28,
                                                mb: 2,
                                            }}
                                        >
                                            {benefit.icon}
                                        </Box>

                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                                            {benefit.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {benefit.description}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Stack>
                </Container>
            </Box>




            {/* CTA Section */}
            <Box sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                color: 'common.white',
                py: 10
            }}>
                <Container maxWidth="md">
                    <Stack spacing={4} alignItems="center" textAlign="center">
                        <Typography variant="h3" component="h2" sx={{
                            fontWeight: 700,
                            fontSize: { xs: '2rem', md: '2.5rem' }
                        }}>
                            Ready to Launch Your Tech Career?
                        </Typography>

                        <Typography variant="h6" sx={{
                            opacity: 0.9,
                            fontWeight: 400
                        }}>
                            Applications are open for our next cohort starting soon. Limited seats available.
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button
                                variant="contained"
                                color="warning"
                                size="large"
                                onClick={()=>navigate('/get-in-touch')}
                                sx={{
                                    px: 6,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600
                                }}
                            >
                                Apply Now
                            </Button>

                            <Button
                                variant="outlined"
                                color="inherit"
                                size="large"
                                sx={{
                                    px: 6,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderWidth: '2px',
                                    '&:hover': {
                                        borderWidth: '2px'
                                    }
                                }}
                            >
                                Download Brochure
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
};

export default InternshipPage;